/**
 * ClientDesk sub-modules.
 *
 * These sit on top of the client master in types.ts and cover the operational
 * layer: what work is being done for the client, what is being charged, and
 * how the client and firm communicate.
 *
 * The task engine here is practice-centric, not generic. A "task" is always
 * a service being delivered to a client (GSTR-1, audit, ITR) and it carries
 * the compliance item it relates to, so task completion and filing status move
 * together. Practive disconnects these — a task can be "completed" while the
 * return is still unfiled, which produces the confusion CA firms complain about.
 */

import { round2 } from "../sales/tax";
import type { ReturnType, FilingStatus } from "./types";

// -------------------------------------------------------------------- Tasks

export type TaskPriority = "low" | "normal" | "high" | "critical";
export type TaskStatus = "pending" | "in-progress" | "on-hold" | "completed" | "verified" | "cancelled";
export type TaskFrequency = "one-time" | "monthly" | "quarterly" | "half-yearly" | "annually";

export interface TaskChecklistItem {
  id: string;
  label: string;
  done: boolean;
  doneBy?: string;
  doneAt?: string;
}

export interface TaskComment {
  id: string;
  author: string;
  text: string;
  at: string;
  /** @mentions trigger notifications to the named users. */
  mentions?: string[];
}

export interface TimeEntry {
  id: string;
  taskId: string;
  user: string;
  startAt: string;
  endAt?: string;
  minutes: number;
  billable: boolean;
  notes?: string;
}

export interface ClientTask {
  id: string;
  clientId: string;
  clientName: string;
  service: string;
  returnType?: ReturnType;
  /** Period this task covers, e.g. "Jul 2026" or "AY 2026-27". */
  period?: string;
  status: TaskStatus;
  priority: TaskPriority;
  assignedTo: string;
  /** Secondary reviewer — must verify before the task can close. */
  reviewer?: string;
  dueDate: string;
  /** Internal target, earlier than due date. */
  targetDate?: string;
  completedAt?: string;
  verifiedAt?: string;
  verifiedBy?: string;
  checklist?: TaskChecklistItem[];
  comments?: TaskComment[];
  timeEntries?: TimeEntry[];
  /** Documents required from the client for this task. */
  documentRequests?: Array<{ id: string; label: string; received: boolean; receivedAt?: string }>;
  /** Set once the task is completed and the return has actually been filed. */
  filingStatus?: FilingStatus;
  acknowledgmentNumber?: string;
  billable: boolean;
  billed?: boolean;
  amount?: number;
  tags?: string[];
  notes?: string;
  /** Link back to the compliance calendar item this task services. */
  complianceItemId?: string;
  /** Auto-task settings if this was created by a recurring schedule. */
  recurringTemplateId?: string;
  createdAt: string;
  updatedAt?: string;
}

export interface RecurringTaskTemplate {
  id: string;
  service: string;
  returnType?: ReturnType;
  frequency: TaskFrequency;
  /** Day of month on which the task is created each cycle. */
  creationDay: number;
  /** Day of month the task is due. */
  dueDay: number;
  assignedTo: string;
  reviewer?: string;
  checklist?: Array<{ label: string }>;
  documentRequests?: Array<{ label: string }>;
  billable: boolean;
  amount?: number;
  tags?: string[];
  active: boolean;
  /** Client ids this template generates tasks for. */
  clientIds: string[];
}

export const taskMinutes = (task: ClientTask): number =>
  (task.timeEntries ?? []).reduce((sum, entry) => sum + entry.minutes, 0);

export const checklistProgress = (task: ClientTask): { done: number; total: number; percent: number } => {
  const items = task.checklist ?? [];
  const done = items.filter((item) => item.done).length;
  return { done, total: items.length, percent: items.length ? Math.round((done / items.length) * 100) : 0 };
};

// ------------------------------------------------------------------ Billing

export type BillingStatus = "unbilled" | "invoiced" | "paid" | "overdue" | "waived";

export interface ClientBillingRecord {
  id: string;
  clientId: string;
  clientName: string;
  period: string;
  service: string;
  amount: number;
  gstRate: number;
  gstAmount: number;
  total: number;
  status: BillingStatus;
  invoiceId?: string;
  invoiceDate?: string;
  dueDate?: string;
  paidDate?: string;
  taskId?: string;
  notes?: string;
}

export interface RetainerAgreement {
  id: string;
  clientId: string;
  name: string;
  services: string[];
  startDate: string;
  endDate: string;
  totalAmount: number;
  frequency: TaskFrequency;
  /** Invoices generated per frequency. */
  invoiceAmount: number;
  billingMode: "prepaid" | "postpaid";
  autoInvoice: boolean;
  autoRenew: boolean;
  status: "active" | "expired" | "cancelled";
}

export interface BillingSummary {
  clientId: string;
  asAt: string;
  totalBilled: number;
  totalPaid: number;
  outstanding: number;
  overdueAmount: number;
  unbilledTasks: number;
  unbilledAmount: number;
  lastInvoiceDate?: string;
  lastPaymentDate?: string;
}

export const buildBillingSummary = (
  clientId: string,
  records: ClientBillingRecord[],
  asAt: string
): BillingSummary => {
  const mine = records.filter((record) => record.clientId === clientId);
  const totalBilled = round2(mine.filter((r) => r.status !== "unbilled" && r.status !== "waived").reduce((sum, r) => sum + r.total, 0));
  const totalPaid = round2(mine.filter((r) => r.status === "paid").reduce((sum, r) => sum + r.total, 0));
  const overdueAmount = round2(mine.filter((r) => r.status === "overdue").reduce((sum, r) => sum + r.total, 0));
  const unbilled = mine.filter((r) => r.status === "unbilled");
  const invoiceDates = mine.filter((r) => r.invoiceDate).map((r) => r.invoiceDate!).sort();
  const paymentDates = mine.filter((r) => r.paidDate).map((r) => r.paidDate!).sort();
  return {
    clientId, asAt,
    totalBilled, totalPaid,
    outstanding: round2(totalBilled - totalPaid),
    overdueAmount,
    unbilledTasks: unbilled.length,
    unbilledAmount: round2(unbilled.reduce((sum, r) => sum + r.amount, 0)),
    lastInvoiceDate: invoiceDates[invoiceDates.length - 1],
    lastPaymentDate: paymentDates[paymentDates.length - 1]
  };
};

// ------------------------------------------------------------- Queries

/**
 * Client queries are tracked separately from task comments. A query is a
 * question or request from the client that needs a firm response and carries
 * an SLA. Unanswered queries beyond 48 hours factor into the relationship
 * health score — they are the single most common source of client complaints.
 */
export type QueryStatus = "open" | "in-progress" | "answered" | "closed";
export type QueryChannel = "whatsapp" | "email" | "phone" | "portal" | "in-person";

export interface ClientQuery {
  id: string;
  clientId: string;
  clientName: string;
  subject: string;
  description: string;
  channel: QueryChannel;
  status: QueryStatus;
  priority: TaskPriority;
  assignedTo?: string;
  receivedAt: string;
  respondedAt?: string;
  resolvedAt?: string;
  /** Hours. Breach triggers an alert to the partner. */
  slaHours?: number;
  tags?: string[];
  linkedTaskId?: string;
}

export const queryIsBreached = (query: ClientQuery, asAt: string): boolean => {
  if (query.status === "answered" || query.status === "closed") return false;
  if (!query.slaHours) return false;
  const elapsed = (new Date(asAt).getTime() - new Date(query.receivedAt).getTime()) / 3_600_000;
  return elapsed > query.slaHours;
};

// -------------------------------------------------------- Notifications / alerts

export type AlertType =
  | "dsc-expiring"
  | "return-due-soon"
  | "return-overdue"
  | "query-sla-breached"
  | "receivable-overdue"
  | "task-overdue"
  | "no-contact"
  | "retainer-expiring"
  | "pan-not-linked";

export interface ClientAlert {
  id: string;
  clientId: string;
  clientName: string;
  type: AlertType;
  severity: "info" | "warning" | "critical";
  message: string;
  dueDate?: string;
  actionUrl?: string;
  dismissed?: boolean;
  createdAt: string;
}

/**
 * Generates alerts for a client from all data sources. This is the function
 * that makes ClientDesk proactive rather than reactive — instead of waiting
 * for something to go wrong, it tells the CA what is about to go wrong.
 */
export const generateAlerts = (input: {
  client: { id: string; name: string; pan?: string; dsc?: Array<{ validTo: string; holderName: string; status: string }> };
  complianceItems: Array<{ returnType: ReturnType; periodLabel: string; dueDate: string; status: string }>;
  openQueries: ClientQuery[];
  overdueReceivable: number;
  overdueTasks: ClientTask[];
  retainers: Array<{ name: string; endDate: string }>;
  daysSinceContact: number;
  asAt: string;
}): ClientAlert[] => {
  const alerts: ClientAlert[] = [];
  const add = (type: AlertType, severity: ClientAlert["severity"], message: string, dueDate?: string) =>
    alerts.push({ id: `${type}-${Date.now()}-${Math.random().toString(36).slice(2)}`, clientId: input.client.id, clientName: input.client.name, type, severity, message, dueDate, createdAt: input.asAt });

  if (!input.client.pan) add("pan-not-linked", "warning", "PAN is not on file. TDS deductions and ITR cannot be tracked.");

  for (const dsc of input.client.dsc ?? []) {
    if (dsc.status === "expired") add("dsc-expiring", "critical", `DSC of ${dsc.holderName} has expired.`, dsc.validTo);
    else if (dsc.status === "expiring-soon") add("dsc-expiring", "warning", `DSC of ${dsc.holderName} expires on ${dsc.validTo}.`, dsc.validTo);
  }

  for (const item of input.complianceItems) {
    if (item.status === "overdue") add("return-overdue", "critical", `${item.returnType} for ${item.periodLabel} is overdue (due ${item.dueDate}).`, item.dueDate);
    else if (item.status === "due-soon") add("return-due-soon", "warning", `${item.returnType} for ${item.periodLabel} is due on ${item.dueDate}.`, item.dueDate);
  }

  const breachedQueries = input.openQueries.filter((q) => queryIsBreached(q, input.asAt));
  if (breachedQueries.length > 0) add("query-sla-breached", "critical", `${breachedQueries.length} client quer${breachedQueries.length === 1 ? "y" : "ies"} past the SLA response time.`);

  if (input.overdueReceivable > 0) add("receivable-overdue", "warning", `₹${input.overdueReceivable.toLocaleString("en-IN")} overdue beyond credit period.`);

  if (input.overdueTasks.length > 0) add("task-overdue", "warning", `${input.overdueTasks.length} task${input.overdueTasks.length === 1 ? "" : "s"} past due date.`);

  for (const retainer of input.retainers) {
    const daysLeft = Math.floor((new Date(retainer.endDate).getTime() - new Date(input.asAt).getTime()) / 86_400_000);
    if (daysLeft <= 30 && daysLeft >= 0) add("retainer-expiring", "info", `Retainer "${retainer.name}" expires on ${retainer.endDate}.`, retainer.endDate);
  }

  if (input.daysSinceContact > 90) add("no-contact", "warning", `No contact with this client in ${input.daysSinceContact} days.`);

  return alerts.sort((a, b) => {
    const order = { critical: 0, warning: 1, info: 2 };
    return order[a.severity] - order[b.severity];
  });
};

// ------------------------------------------------------- Practice dashboard

export interface PracticeSummary {
  asAt: string;
  totalClients: number;
  activeClients: number;
  tasksOverdue: number;
  tasksDueToday: number;
  tasksDueThisWeek: number;
  returnsOverdue: number;
  returnsDueSoon: number;
  dscExpiring: number;
  unbilledAmount: number;
  overdueReceivable: number;
  clientsAtRisk: number;
  topPerformers: Array<{ user: string; completedTasks: number; totalTasks: number }>;
}

export const buildPracticeSummary = (
  clients: Array<{ id: string; status: string }>,
  tasks: ClientTask[],
  complianceItems: Array<{ status: string; dueDate: string }>,
  alerts: ClientAlert[],
  billingRecords: ClientBillingRecord[],
  asAt: string
): PracticeSummary => {
  const today = asAt.slice(0, 10);
  const weekEnd = new Date(new Date(asAt).getTime() + 7 * 86_400_000).toISOString().slice(0, 10);
  const activeTasks = tasks.filter((t) => t.status !== "cancelled" && t.status !== "verified");

  const byUser = new Map<string, { completed: number; total: number }>();
  for (const task of tasks.filter((t) => t.status !== "cancelled")) {
    const entry = byUser.get(task.assignedTo) ?? { completed: 0, total: 0 };
    entry.total += 1;
    if (task.status === "completed" || task.status === "verified") entry.completed += 1;
    byUser.set(task.assignedTo, entry);
  }

  return {
    asAt,
    totalClients: clients.length,
    activeClients: clients.filter((c) => c.status === "active").length,
    tasksOverdue: activeTasks.filter((t) => t.dueDate < today && t.status !== "completed").length,
    tasksDueToday: activeTasks.filter((t) => t.dueDate === today).length,
    tasksDueThisWeek: activeTasks.filter((t) => t.dueDate > today && t.dueDate <= weekEnd).length,
    returnsOverdue: complianceItems.filter((i) => i.status === "overdue").length,
    returnsDueSoon: complianceItems.filter((i) => i.status === "due-soon").length,
    dscExpiring: alerts.filter((a) => a.type === "dsc-expiring").length,
    unbilledAmount: round2(billingRecords.filter((r) => r.status === "unbilled").reduce((sum, r) => sum + r.total, 0)),
    overdueReceivable: round2(billingRecords.filter((r) => r.status === "overdue").reduce((sum, r) => sum + r.total, 0)),
    clientsAtRisk: alerts.filter((a) => a.severity === "critical").map((a) => a.clientId).filter((v, i, arr) => arr.indexOf(v) === i).length,
    topPerformers: [...byUser.entries()]
      .map(([user, stats]) => ({ user, completedTasks: stats.completed, totalTasks: stats.total }))
      .sort((a, b) => b.completedTasks - a.completedTasks)
      .slice(0, 5)
  };
};
